import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const products = [
  {
    category: 'Biologics & Allografts',
    icon: 'ri-drop-fill',
    color: '#002868',
    items: [
      {
        name: 'Amniotic Membrane Allografts',
        desc: 'Dehydrated and cryopreserved amniotic membrane products used for wound healing, orthopedic, and ophthalmologic applications. Full COA provided.',
        badge: 'Top Seller',
      },
      {
        name: 'Wharton\'s Jelly (MSCs)',
        desc: 'Mesenchymal stem cell products derived from umbilical cord Wharton\'s Jelly — a premier regenerative biologic for joint, spine, and systemic applications.',
        badge: 'Premium',
      },
      {
        name: 'Exosome Formulations',
        desc: 'Cutting-edge exosome-based biologics offering advanced cell signaling capabilities for tissue regeneration, anti-inflammatory therapy, and aesthetic treatments.',
        badge: 'New',
      },
      {
        name: 'Placental-Derived Growth Factors',
        desc: 'Concentrated growth factor products sourced from placental tissue, supporting accelerated tissue repair, angiogenesis, and cellular proliferation.',
        badge: null,
      },
    ],
  },
  {
    category: 'PRP & Cellular Systems',
    icon: 'ri-flask-fill',
    color: '#b91c1c',
    items: [
      {
        name: 'PRP Collection Kits',
        desc: 'Complete platelet-rich plasma kits designed for clinical use — easy to use, consistent yields, and FDA-cleared for in-office procedures.',
        badge: 'FDA Cleared',
      },
      {
        name: 'Bone Marrow Aspirate Kits',
        desc: 'Bone marrow aspirate concentrate (BMAC) systems for orthopedic and regenerative applications, featuring efficient cell recovery and clinical-grade processing.',
        badge: null,
      },
      {
        name: 'Adipose-Derived Cell Products',
        desc: 'Minimally manipulated adipose tissue products providing a rich source of regenerative cells for musculoskeletal, aesthetic, and systemic therapies.',
        badge: null,
      },
      {
        name: 'Lyophilized Platelet Products',
        desc: 'Shelf-stable lyophilized platelet formulations offering extended shelf life, consistent platelet counts, and ease of storage for busy clinical practices.',
        badge: 'Convenient',
      },
    ],
  },
];

const services = [
  {
    icon: 'ri-stethoscope-fill',
    title: 'Expert Consulting',
    desc: 'Leverage 10+ years of Regenerative Medicine expertise. OnPoint USA provides personalized strategic consulting to help clinics, distributors, and providers navigate product selection, regulatory considerations, and business growth in the regen med space.',
    link: '/contact-us',
  },
  {
    icon: 'ri-book-open-fill',
    title: 'Education & Training',
    desc: 'We offer comprehensive education and training programs on Regenerative Medicine products, protocols, and best practices. From introductory workshops to advanced clinical training, we equip your team with the knowledge to succeed.',
    link: '/contact-us',
  },
  {
    icon: 'ri-shield-star-fill',
    title: 'Regulatory Guidance',
    desc: 'Navigating FDA regulations for biologics and regenerative products can be complex. Our team provides clear, practical guidance to help your practice stay compliant, avoid common pitfalls, and operate with confidence.',
    link: '/contact-us',
  },
  {
    icon: 'ri-team-fill',
    title: 'Distribution Partnerships',
    desc: 'Partner with OnPoint USA for reliable, cost-effective distribution of premium regenerative medicine biologics. Our extensive network and 10 years of connections mean you get the right products, at the right price, when you need them.',
    link: '/contact-us',
  },
  {
    icon: 'ri-customer-service-2-fill',
    title: 'Dedicated Account Support',
    desc: 'Every OnPoint USA client is assigned a dedicated account representative. Real people, real answers — we respond within 24 hours and never outsource our communications. Your success is personal to us.',
    link: '/contact-us',
  },
  {
    icon: 'ri-line-chart-fill',
    title: 'Practice Growth Strategy',
    desc: 'Beyond product supply, we help healthcare practices build and grow their regenerative medicine revenue streams. From marketing support to patient education materials, OnPoint USA is your full-service growth partner.',
    link: '/contact-us',
  },
];

export default function ProductsPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Hero Banner */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=advanced%20regenerative%20medicine%20laboratory%20with%20biological%20cell%20cultures%2C%20scientific%20research%20equipment%2C%20microscopes%20and%20petri%20dishes%20with%20glowing%20biological%20samples%2C%20clean%20modern%20lab%20environment%20with%20blue%20and%20white%20lighting&width=1440&height=576&seq=prod-hero-01&orientation=landscape"
          alt="Products and Services"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/70 via-blue-900/60 to-blue-950/70" />
        <div className="relative z-10 text-center px-6">
          <span className="inline-block bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            OnPoint USA
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Products &amp; Services
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-red-600 text-xs font-bold uppercase tracking-widest">10 Years of Expertise</span>
          <h2 className="text-3xl font-bold mt-2 mb-4" style={{ color: '#002868', fontFamily: 'Montserrat, sans-serif' }}>
            Premium Regenerative Medicine Solutions
          </h2>
          <div className="w-12 h-0.5 bg-red-600 mx-auto mb-6" />
          <p className="text-gray-600 text-sm leading-relaxed">
            OnPoint USA brings together a curated portfolio of the highest-quality regenerative medicine biologics, combined with unparalleled expert services. With 10 years of deep industry experience and extensive connections in the Regenerative Medicine field, we provide healthcare providers, clinics, and distributors with everything they need to deliver exceptional patient outcomes.
          </p>
        </div>
      </section>

      {/* Products */}
      {products.map((category) => (
        <section key={category.category} className="py-12 px-6" style={{ background: category.color === '#002868' ? '#fff' : '#f8f9ff' }}>
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ background: category.color }}>
                <i className={`${category.icon} text-white text-lg`} />
              </div>
              <h2 className="text-2xl font-bold" style={{ color: '#002868', fontFamily: 'Montserrat, sans-serif' }}>
                {category.category}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
              {category.items.map((item) => (
                <div
                  key={item.name}
                  className="bg-white rounded-lg border border-gray-100 p-6 hover:border-red-200 hover:shadow-sm transition-all relative"
                >
                  {item.badge && (
                    <span
                      className="absolute top-4 right-4 text-xs font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ background: item.badge === 'New' ? '#b91c1c' : '#002868' }}
                    >
                      {item.badge}
                    </span>
                  )}
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg mb-4" style={{ background: '#f0f4ff' }}>
                    <i className="ri-leaf-fill text-blue-800" />
                  </div>
                  <h3 className="font-bold text-sm mb-2" style={{ color: '#002868' }}>{item.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  <Link
                    to="/contact-us"
                    className="mt-4 inline-flex items-center gap-1 text-red-600 text-xs font-semibold hover:text-red-700 cursor-pointer"
                  >
                    Inquire Now <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Services */}
      <section className="py-20 px-6" style={{ background: '#002868' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-red-400 text-xs font-bold uppercase tracking-widest">What We Offer</span>
            <h2 className="text-3xl font-bold text-white mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Expert Services
            </h2>
            <div className="w-12 h-0.5 bg-red-500 mx-auto mt-4" />
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {services.map((svc) => (
              <div
                key={svc.title}
                className="rounded-lg p-6 border border-blue-700 bg-blue-900/40 hover:bg-blue-900/60 transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg mb-4 bg-red-600">
                  <i className={`${svc.icon} text-white text-lg`} />
                </div>
                <h3 className="font-bold text-base text-white mb-2">{svc.title}</h3>
                <p className="text-blue-200 text-xs leading-relaxed mb-4">{svc.desc}</p>
                <Link
                  to={svc.link}
                  className="inline-flex items-center gap-1 text-red-400 text-xs font-semibold hover:text-red-300 cursor-pointer"
                >
                  Learn More <i className="ri-arrow-right-line" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#002868', fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Partner With OnPoint USA?
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto mb-8">
            Whether you&apos;re a clinic, distributor, or healthcare provider looking to expand your regenerative medicine capabilities, OnPoint USA has the products, expertise, and connections to take your practice to the next level.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="inline-block text-white font-bold text-sm px-8 py-3 rounded-md hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
              style={{ background: '#dc2626' }}
            >
              Get In Touch
            </Link>
            <Link
              to="/manufacturing-and-distribution"
              className="inline-block font-bold text-sm px-8 py-3 rounded-md border-2 hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
              style={{ color: '#002868', borderColor: '#002868' }}
            >
              Our Distribution Process
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
